import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link as LinkIcon, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

const GreenFrogGroup = () => {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);

  const counties = [
    "Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "City of London", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "County Durham", "East Riding of Yorkshire", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire",
    "Blaenau Gwent", "Bridgend", "Caerphilly", "Cardiff", "Carmarthenshire", "Ceredigion", "Conwy", "Denbighshire", "Flintshire", "Gwynedd", "Isle of Anglesey", "Merthyr Tydfil", "Monmouthshire", "Neath Port Talbot", "Newport", "Pembrokeshire", "Powys", "Rhondda Cynon Taf", "Swansea", "Torfaen", "Vale of Glamorgan", "Wrexham"
  ];

  const metaDescription = "How Green Frog Utilities and Green Frog Asset Management work together: the connection is designed and built under GIRS, WIRS and NERS, and the meter is installed, owned and managed by our MAM and AMI accredited metering company. Two companies, one programme.";

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Green Frog Utilities",
    "legalName": "GREEN FROG UTILITIES LIMITED",
    "foundingDate": "2014",
    "url": "https://greenfrogutilities.co.uk",
    "subOrganization": {
      "@type": "Organization",
      "name": "Green Frog Asset Management",
      "legalName": "GREEN FROG ASSET MANAGEMENT LIMITED",
      "foundingDate": "2015",
      "url": "https://greenfrogassetmanagement.co.uk",
      "sameAs": "https://greenfrogassetmanagement.co.uk"
    },
    "sameAs": "https://greenfrogassetmanagement.co.uk"
  };

  const faqData = [
    {
      question: "Are Green Frog Utilities and Green Frog Asset Management the same company?",
      answer: "No. They are two separate limited companies that work together inside one group. Green Frog Utilities delivers gas, water and electrical connections. Green Frog Asset Management installs, owns and manages meters."
    },
    {
      question: "Why is the metering in a separate company?",
      answer: "Because a meter asset manager holds the asset for decades. Keeping that function in its own accredited company means cleaner records, clearer liability and direct relationships with networks and suppliers."
    },
    {
      question: "Can I use both companies on the same project?",
      answer: "Yes. That is the point. The connection programme and the metering programme are designed together, so the meter is ready when the connection lands, not months later."
    },
    {
      question: "Do I get two contracts and two invoices?",
      answer: "You get clarity on who is doing what. Whether that is one combined quotation or two separate ones depends on the project and what suits the buyer."
    },
    {
      question: "Who should I call first?",
      answer: "Either company. They share an office and talk to each other. If you contact Green Frog Utilities about a connection that needs a meter, the metering side is brought in automatically."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Utilities and Asset Management", "item": "https://greenfrogutilities.co.uk/green-frog-group" }
    ]
  };

  const workflowSteps = [
    {
      step: "1",
      title: "Enquiry",
      desc: "The site tells us what utilities and metering it needs."
    },
    {
      step: "2",
      title: "Design",
      desc: "Green Frog Utilities designs the gas, water or electrical connection. Green Frog Asset Management sizes the meter to match."
    },
    {
      step: "3",
      title: "Installation",
      desc: "The connection is laid by Green Frog Utilities under GIRS, WIRS or NERS. The meter is installed by Green Frog Asset Management under its AMI accreditation."
    },
    {
      step: "4",
      title: "Handover",
      desc: "The site has a live connection and a registered, reading meter on the same day."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div>
      <SEO 
        title="Green Frog Utilities and Green Frog Asset Management | One Group, Connection to Meter"
        description={metaDescription}
        canonical="/green-frog-group"
        jsonLd={[organizationSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <LinkIcon className="h-16 w-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Two companies, one group, one connection
            </h1>
            <p className="text-xl text-emerald-100 max-w-4xl mx-auto leading-relaxed">
              Green Frog Utilities delivers the connection. Green Frog Asset Management delivers the meter. Same group, same programme, same handover date.
            </p>
          </div>
        </div>
      </section>

      {/* Two Companies Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* GFU Card */}
            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="flex justify-center mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/zmxuftfb_GFU_logo-removebg-preview.png"
                    alt="Green Frog Utilities logo"
                    className="h-16 object-contain"
                  />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Green Frog Utilities</h2>
                <p className="text-gray-600 mb-4">
                  Green Frog Utilities Limited, founded 2014, is the multi utility contractor. It designs and builds gas, water and electrical connections under GIRS, WIRS and NERS accreditation. The work it delivers is adopted by the public network.
                </p>
                <p className="text-sm text-gray-500 text-center">
                  Registered in England, company 09119882, founded 2014
                </p>
              </CardContent>
            </Card>

            {/* GFAM Card */}
            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="flex justify-center mb-6">
                  <img 
                    src="https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/v2cfq0ho_GFAM-logo-white-bg.png"
                    alt="Green Frog Asset Management logo"
                    className="h-16 object-contain"
                  />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Green Frog Asset Management</h2>
                <p className="text-gray-600 mb-4">
                  Green Frog Asset Management Limited, founded 2015, is the metering company. It installs, owns and manages commercial and industrial gas meters under MAM and AMI accreditation, and holds Cyber Essentials certification.
                </p>
                <p className="text-sm text-gray-500 text-center mb-4">
                  Registered in England, company 09796253, founded 2015
                </p>
                <div className="text-center">
                  <a 
                    href="https://greenfrogassetmanagement.co.uk" 
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Visit greenfrogassetmanagement.co.uk &rarr;
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why this matters to you
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Most connection projects are handed to one contractor and then the meter is left to someone else at the end. That creates a gap: different companies, different programmes, different handover dates. By keeping both inside Green Frog Utilities and Green Frog Asset Management, the two companies remove the gap. The connection and the meter are designed together and delivered together, with one point of contact for both.
            </p>
          </div>
        </div>
      </section>

      {/* How They Work Together Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How the two companies work together
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflowSteps.map((phase, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-emerald-300 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                <p className="text-emerald-100">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where We Work Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Where we work</h2>
          <p className="text-gray-600 mb-6">
            Green Frog Utilities delivers gas, water and electrical connections across England and Wales, from a single accredited contractor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
            {counties.map((county, index) => (
              <span key={index} className="text-sm text-gray-500">{county}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to start your project?
            </h2>
            <Link to="/contact">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GreenFrogGroup;
